import { useQuery } from '@tanstack/react-query'

export const KEY_STORAGE = 'zeus_kiosk_key'

export class KioskAuthError extends Error {}

/** Pure: pull `k=<key>` out of a location.hash fragment string. */
export function parseKioskKeyFromHash(hash: string): string | null {
  const frag = hash.startsWith('#') ? hash.slice(1) : hash
  const params = new URLSearchParams(frag)
  const k = params.get('k')
  return k && k.length > 0 ? k : null
}

/** Read the key from the URL fragment (persisting + cleaning), else localStorage. */
export function initKioskKey(): string | null {
  const fromHash = parseKioskKeyFromHash(window.location.hash)
  if (fromHash) {
    localStorage.setItem(KEY_STORAGE, fromHash)
    // strip the fragment so the key never lingers in the visible URL
    history.replaceState(null, '', window.location.pathname + window.location.search)
    return fromHash
  }
  return localStorage.getItem(KEY_STORAGE)
}

export function getKioskKey(): string | null {
  return localStorage.getItem(KEY_STORAGE)
}

export async function kioskFetch<T>(path: string): Promise<T> {
  const key = getKioskKey()
  if (!key) throw new KioskAuthError('no kiosk key')
  const res = await fetch(path, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${key}` },
  })
  if (res.status === 401 || res.status === 403) throw new KioskAuthError(`auth ${res.status}`)
  if (!res.ok) throw new Error(`kiosk fetch ${path}: ${res.status}`)
  return (await res.json()) as T
}

// --- Types (subset of the endpoint payloads the kiosk uses) ---

export interface KioskSummary {
  cluster: { nodes: number; services: number; stacks: number }
  alerts: { open: number; critical: number }
  backups: { ok_24h: number; failed_24h: number }
  findings: { active: number }
  recent_alerts: { id: string; severity: string; title: string; opened_at: string; closed_at: string | null }[]
}

export interface KioskPower {
  online: boolean
  power_w: number | null
  currency: string
  today: { kwh: number; cost: number }
  // Backend-computed, scaled by the elapsed fraction of the month; null until
  // enough of the month has passed to extrapolate.
  projection: { month_kwh: number | null; month_cost: number | null }
}

export interface KioskPowerSeries {
  points: { b: string; power_w: number | null; online: boolean }[]
}

export interface KioskGpuSummary {
  total_gpus: number
  online_gpus: number
  active_gpus: number
  avg_util_pct: number | null
  total_mem_used: number
  total_mem_capacity: number
  hottest_temp_c: number | null
}

export interface KioskGpu {
  gpu_index: number
  gpu_name: string | null
  util_pct: number | null
  mem_used: number | null
  mem_total: number | null
  temp_c: number | null
  power_w: number | null
}

export interface KioskNode {
  id: string
  name: string
  status: string
  latest_metrics: {
    ts: string
    cpu_pct: number | null
    load1: number | null
    mem_used: number | null
    mem_total: number | null
  } | null
  gpus: KioskGpu[] | null
}

/** A node is "up" only if it produced a metric sample recently. The backend
 * `status` field can stay stale-"active" for a node that stopped reporting
 * (e.g. node5, the out-of-swarm Mac, whose last sample can be days old), so
 * freshness — not `status` — is the truth the kiosk shows. */
export function nodeFresh(n: KioskNode): boolean {
  const ts = n.latest_metrics?.ts
  return ts != null && Date.now() - Date.parse(ts) < 120_000
}

export interface KioskStack {
  name: string
  services_count: number
}

export interface KioskFreebox {
  boxes: {
    source: string
    wan_state: string | null
    backup_4g: boolean
    rate_down_bytes_s: number | null
    rate_up_bytes_s: number | null
    sfp_pwr_rx_dbm: number | null
  }[]
}

export interface KioskEnvTemp {
  sensors: {
    sensor_id: string
    inside: { ts: string; temp_c: number | null } | null
    outside: { ts: string; temp_c: number | null } | null
    delta_c: number | null
  }[]
}

export interface KioskMikrotik {
  temp_thresholds: { warn_c: number; crit_c: number }
  devices: {
    device: string
    reachable: boolean
    temp_board_c: number | null
    ports: {
      port: string
      link: boolean
      sfp_temp_c: number | null
      neighbor: { name: string | null; ip: string | null } | null
    }[]
  }[]
}

const REFETCH_FAST = 5_000
const REFETCH = 10_000
const REFETCH_SLOW = 30_000

export function useKioskSummary() {
  return useQuery<KioskSummary>({ queryKey: ['k-summary'], queryFn: () => kioskFetch('/api/summary'), refetchInterval: REFETCH })
}
export function useKioskPower() {
  return useQuery<KioskPower>({ queryKey: ['k-power'], queryFn: () => kioskFetch('/api/metrics/power'), refetchInterval: REFETCH_FAST })
}
export function useKioskPowerSeries(range = '24h') {
  return useQuery<KioskPowerSeries>({ queryKey: ['k-power-series', range], queryFn: () => kioskFetch(`/api/metrics/power/series?range=${range}`), refetchInterval: REFETCH_SLOW })
}
export function useKioskGpuSummary() {
  return useQuery<KioskGpuSummary>({ queryKey: ['k-gpu-sum'], queryFn: () => kioskFetch('/api/cluster/gpus/summary'), refetchInterval: REFETCH_FAST })
}
export function useKioskNodes() {
  return useQuery<KioskNode[]>({ queryKey: ['k-nodes'], queryFn: () => kioskFetch('/api/cluster/nodes'), refetchInterval: REFETCH_FAST })
}
export function useKioskStacks() {
  return useQuery<KioskStack[]>({ queryKey: ['k-stacks'], queryFn: () => kioskFetch('/api/cluster/stacks'), refetchInterval: REFETCH_SLOW })
}
export function useKioskFreebox() {
  return useQuery<KioskFreebox>({ queryKey: ['k-freebox'], queryFn: () => kioskFetch('/api/metrics/freebox'), refetchInterval: REFETCH_SLOW })
}
export function useKioskEnvTemp() {
  return useQuery<KioskEnvTemp>({ queryKey: ['k-env-temp'], queryFn: () => kioskFetch('/api/metrics/env-temp'), refetchInterval: REFETCH_SLOW })
}
export function useKioskMikrotik() {
  return useQuery<KioskMikrotik>({ queryKey: ['k-mikrotik'], queryFn: () => kioskFetch('/api/metrics/mikrotik'), refetchInterval: REFETCH_SLOW })
}
